<template>
	<div class="marketing-materials-tab">
		<!-- 左侧 -->
		<aside class="mm-topics" aria-label="营销话题列表">
			<div class="mm-topics__controls">
				<div class="mm-enable-row">
					<span class="mm-enable-row__label">启用</span>
					<label class="mm-switch">
						<input
							type="checkbox"
							:checked="weeklyDigestEnabled"
							:disabled="configSaving"
							@change="onMarketingConfigToggle"
						/>
						<span class="mm-switch__slider" />
					</label>
					<span class="mm-enable-row__hint">（每周一定时自动智能归纳）</span>
				</div>
				<div class="mm-week-nav" role="group" aria-label="按周选择日期">
					<button type="button" class="mm-week-nav__edge" @click="shiftWeek(-1)">上一周</button>
					<span class="mm-week-nav__range">{{ weekRangeLabel }}</span>
					<button
						type="button"
						class="mm-week-nav__edge"
						:disabled="isViewingCurrentWeek"
						@click="shiftWeek(1)"
					>
						下一周
					</button>
				</div>
			</div>
			<div class="mm-topics__list">
				<div v-if="summaryLoading" class="mm-topics__hint">加载中…</div>
				<div v-else-if="!summaries.length" class="mm-topics__hint">本周未找到有价值的营销素材</div>
				<template v-else>
					<div
						v-for="summary in summaries"
						:key="summary.id"
						role="button"
						tabindex="0"
						class="mm-topic-card"
						:class="{
							'mm-topic-card--active': String(selectedSummaryId) === String(summary.id),
							'mm-topic-card--failed': summary.status === 'failed'
						}"
						@click="selectSummary(summary)"
						@keydown.enter.prevent="selectSummary(summary)"
						@keydown.space.prevent="selectSummary(summary)"
					>
						<div class="mm-topic-card__head">
							<img :src="summaryAvatar(summary)" alt="" class="mm-topic-card__avatar" />
							<span class="mm-topic-card__title">{{ summaryTitle(summary) }}</span>
						</div>
						<div class="mm-topic-card__body message-bubble">
							<div
								class="mm-topic-card__summary-md"
								v-html="formatSummaryMarkdown(summary.summary)"
								@click="onTopicSummaryMarkdownClick"
							></div>
						</div>
					</div>
				</template>
			</div>
		</aside>

		<div class="mm-workspace-container">
			<!-- 中间：生成与结果 -->
			<section class="mm-workspace" aria-label="素材生成">
				<div class="mm-workspace__inner">
					<div class="mm-workspace__top">
					<div class="mm-greeting">
						<img src="@/assets/images/default_avatar.jpg" alt="" class="mm-greeting__icon" />
						<h2 class="mm-greeting__text">你好，今天有什么可以帮到你？</h2>
					</div>

					<div
						v-if="selectedSummary"
						class="mm-context"
					>
						<img :src="summaryAvatar(selectedSummary)" alt="" class="mm-context__avatar" />
						<span class="mm-context__topic">{{ summaryTitle(selectedSummary) }}</span>
						<label class="mm-context__check">
							<input type="checkbox" :checked="true" disabled />
							已选中对应聊天内容
						</label>
					</div>
					<div v-else class="mm-context mm-context--empty">
						请先选择左侧对应话题，才能开始整理输出素材
					</div>

					<div class="mm-actions">
						<button
							v-for="label in quickActions"
							:key="label"
							type="button"
							class="mm-actions__btn"
							:disabled="!selectedSummary || materialSending"
							@click="applyQuickAction(label)"
						>
							{{ label }}
						</button>
					</div>

					<div
						class="mm-input-panel"
						:class="{ 'mm-input-panel--disabled': !selectedSummary }"
					>
						<textarea
							v-model="userHint"
							class="mm-input-panel__textarea"
							rows="5"
							maxlength="5000"
							:disabled="!selectedSummary || materialSending"
							placeholder="提示：您可以在这里输入你希望修改调整的具体思路（如：请你把点赞的具体内容更详细的描述，并引导大家关注）"
						/>
						<button
							type="button"
							class="mm-input-panel__send"
							aria-label="发送"
							:disabled="!selectedSummary || materialSending"
							@click="submitMaterial()"
						>
							<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M22 2L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
						</button>
					</div>
					</div>

					<!-- 仅列表区域滚动，上方问候/上下文/输入区固定 -->
					<div class="mm-material-list-scroll">
					<ul v-if="materialList.length" class="mm-material-list" aria-label="生成结果">
						<li
							v-for="item in materialList"
							:key="item.id"
							class="mm-material-item"
							:class="{ 'mm-material-item--failed': item.rawStatus === 'failed' }"
						>
							<img :src="item.avatar" alt="" class="mm-material-item__avatar" />
							<div class="mm-material-item__col">
								<div class="mm-material-item__head">
									<span class="mm-material-item__time">{{ item.createdAt }}</span>
									<button type="button" class="mm-material-item__copy" @click="copyMaterial(item)">复制</button>
								</div>
								<div class="mm-material-item__body">{{ item.content }}</div>
							</div>
						</li>
					</ul>
					</div>
				</div>
			</section>
		</div>

		<!-- 右侧：与 mm-topics 相同 fixed 定位，主区仅用 padding 留白 -->
		<aside class="mm-chat" aria-label="聊天详情">
			<h3 class="mm-chat__title">聊天详情</h3>
			<div class="mm-chat__thread">
				<div v-if="chatLoading" class="mm-chat__empty">加载中…</div>
				<div v-else-if="!chatMessages.length" class="mm-chat__empty">暂无聊天记录</div>
				<template v-else>
					<div
						v-for="(msg, idx) in chatMessages"
						:key="msg.id || idx"
						class="mm-msg"
						:class="msg.role === 'user' ? 'mm-msg--user' : 'mm-msg--assistant'"
					>
						<img :src="msg.avatar" alt="" class="mm-msg__avatar" />
						<div class="mm-msg__col">
							<div class="mm-msg__body">
								<div class="mm-msg__time">{{ msg.time }}</div>
								<div
									class="mm-msg__bubble"
									:aria-label="`${msg.sender}：${msg.text}`"
								>
									<p class="mm-msg__text">{{ msg.text }}</p>
								</div>
							</div>
						</div>
					</div>
				</template>
			</div>
		</aside>
	</div>
</template>

<script>
import { marked } from 'marked';

const MAX_USER_PROMPT = 5000;

marked.setOptions({
	breaks: true,
	gfm: true,
	silent: true
});

function formatDate(d) {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

/** 当周周一 00:00（本地），自然周为周一至周日 */
function startOfWeekMonday(ref = new Date()) {
	const c = new Date(ref);
	const day = c.getDay();
	const diff = day === 0 ? -6 : 1 - day;
	c.setDate(c.getDate() + diff);
	c.setHours(0, 0, 0, 0);
	return c;
}

/** 相对 ref 所在周，上一自然周的周一 00:00（本周数据往往未就绪，列表默认看上周） */
function startOfLastWeekMonday(ref = new Date()) {
	const mon = startOfWeekMonday(ref);
	mon.setDate(mon.getDate() - 7);
	return mon;
}

/** 与周一同属一周的周日本地 00:00（周一 + 6 天） */
function endOfWeekSunday(monday) {
	const e = new Date(monday);
	e.setDate(e.getDate() + 6);
	e.setHours(0, 0, 0, 0);
	return e;
}

/** 周导航展示：2026.4.13（不补零月日） */
function formatWeekNavDot(d) {
	const x = d instanceof Date ? new Date(d.getTime()) : new Date(d);
	return `${x.getFullYear()}.${x.getMonth() + 1}.${x.getDate()}`;
}

export default {
	name: 'MarketingMaterialsTab',
	data() {
		return {
			botNumber: 1,
			weeklyDigestEnabled: false,
			configSaving: false,
			weekStart: startOfLastWeekMonday(),
			selectedSummaryId: null,
			userHint: '',
			materialList: [],
			materialSending: false,
			summaries: [],
			summaryLoading: false,
			chatMessages: [],
			chatLoading: false,
			quickActions: [
				'帮我生成一个视频脚本',
				'帮我写一个小红书文案',
				'帮我写个朋友圈文案'
			],
			defaultTopicAvatar: require('@/assets/images/default_avatar.jpg')
		};
	},
	computed: {
		/** 当前选中周的周日（与 weekStart 同一自然周） */
		weekEndSunday() {
			return endOfWeekSunday(this.weekStart);
		},
		/** 完整周一至周日，如 2026.4.13-2026.4.19 */
		weekRangeLabel() {
			const start = new Date(this.weekStart);
			start.setHours(0, 0, 0, 0);
			return `${formatWeekNavDot(start)}-${formatWeekNavDot(this.weekEndSunday)}`;
		},
		/** 与「今天」所在自然周一致时不可再选未来周 */
		isViewingCurrentWeek() {
			const cur = new Date(this.weekStart);
			cur.setHours(0, 0, 0, 0);
			const thisWeekMon = startOfWeekMonday(new Date());
			return cur.getTime() === thisWeekMon.getTime();
		},
		selectedSummary() {
			return this.summaries.find(s => String(s.id) === String(this.selectedSummaryId)) || null;
		}
	},
	watch: {
		weekStart() {
			this.fetchSummaries();
		}
	},
	mounted() {
		this.fetchMarketingConfig();
		this.fetchSummaries();
	},
	methods: {
		apiMsg(res) {
			return res.msg || res.message || '';
		},
		summaryTitle(s) {
			if (!s) return '';
			if (s.chat_type === 'private' && s.customer) return s.customer.name || '私聊';
			if (s.chat_type === 'group' && s.group_chat) return s.group_chat.group_name || '群聊';
			return '内容';
		},
		summaryAvatar(s) {
			if (!s) return this.defaultTopicAvatar;
			if (s.chat_type === 'private' && s.customer && s.customer.avatar) return s.customer.avatar;
			if (s.chat_type === 'group' && s.group_chat && s.group_chat.group_avatar) return s.group_chat.group_avatar;
			return this.defaultTopicAvatar;
		},
		/** 摘要正文 Markdown（与 Chat 一致使用 marked） */
		formatSummaryMarkdown(text) {
			if (text == null || String(text).trim() === '') {
				return '<p class="mm-topic-card__md-empty">暂无摘要</p>';
			}
			try {
				return marked.parse(String(text));
			} catch (err) {
				console.error(err);
				return String(text).replace(/\n/g, '<br>');
			}
		},
		/** 点击 Markdown 内链接时不冒泡到卡片，避免拦截跳转 */
		onTopicSummaryMarkdownClick(e) {
			if (e.target && typeof e.target.closest === 'function' && e.target.closest('a')) {
				e.stopPropagation();
			}
		},
		async fetchMarketingConfig() {
			try {
				const res = await this.$get('/marketing/config', { bot_number: this.botNumber }, { showLoading: false });
				if (res.code === 200 && res.data) {
					this.weeklyDigestEnabled = !!res.data.enabled;
				}
			} catch (e) {
				console.error(e);
			}
		},
		async onMarketingConfigToggle(e) {
			const next = e.target.checked;
			this.configSaving = true;
			try {
				const res = await this.$put('/marketing/config', {
					bot_number: this.botNumber,
					enabled: next
				});
				if (res.code === 200) {
					const en = res.data && typeof res.data.enabled === 'boolean' ? res.data.enabled : next;
					this.weeklyDigestEnabled = en;
					e.target.checked = en;
					this.$message.success('已更新');
				} else {
					e.target.checked = !next;
					this.$message.warning(this.apiMsg(res) || '更新失败');
				}
			} catch (err) {
				console.error(err);
				e.target.checked = !next;
				this.$message.error('更新失败');
			} finally {
				this.configSaving = false;
			}
		},
		async fetchSummaries() {
			this.summaryLoading = true;
			try {
				const weekStart = new Date(this.weekStart);
				weekStart.setHours(0, 0, 0, 0);
				const weekEnd = endOfWeekSunday(weekStart);
				const res = await this.$get(
					'/marketing/summary',
					{
						bot_number: this.botNumber,
						page: 1,
						page_size: 100,
						start_date: formatDate(weekStart),
						end_date: formatDate(weekEnd)
					},
					{ showLoading: false }
				);
				if (res.code !== 200) {
					this.$message.warning(this.apiMsg(res) || '列表加载失败');
					this.summaries = [];
					return;
				}
				this.summaries = Array.isArray(res.data) ? res.data : [];
				if (
					this.selectedSummaryId &&
					!this.summaries.some(s => String(s.id) === String(this.selectedSummaryId))
				) {
					this.selectedSummaryId = null;
					this.chatMessages = [];
					this.materialList = [];
				}
			} catch (e) {
				console.error(e);
				this.summaries = [];
				this.$message.error('列表加载失败');
			} finally {
				this.summaryLoading = false;
			}
		},
		async selectSummary(summary) {
			this.selectedSummaryId = summary.id;
			await Promise.all([this.fetchSummaryMessages(summary.id), this.fetchMaterialHistory(summary.id)]);
		},
		async fetchSummaryMessages(summaryId) {
			this.chatLoading = true;
			this.chatMessages = [];
			try {
				const res = await this.$get(
					`/marketing/summary/${summaryId}/messages`,
					{ bot_number: this.botNumber },
					{ showLoading: false }
				);
				if (res.code !== 200) {
					this.$message.warning(this.apiMsg(res) || '聊天记录加载失败');
					return;
				}
				const list = Array.isArray(res.data) ? res.data : [];
				const avUser = this.defaultTopicAvatar;
				const avBot = this.defaultTopicAvatar;
				this.chatMessages = list.map(m => {
					const isUser = m.from_type === 'customer' || m.from_type === 'member';
					return {
						id: m.id,
						role: isUser ? 'user' : 'assistant',
						avatar: isUser ? avUser : avBot,
						sender: m.sender_name || (isUser ? '用户' : '助手'),
						time: this.formatMaterialTime(m.created_at),
						text: m.content || ''
					};
				});
			} catch (e) {
				console.error(e);
				this.$message.error('聊天记录加载失败');
			} finally {
				this.chatLoading = false;
			}
		},
		async fetchMaterialHistory(summaryId) {
			try {
				const res = await this.$get(
					`/marketing/summary/${summaryId}/content`,
					{ bot_number: this.botNumber },
					{ showLoading: false }
				);
				if (res.code !== 200) {
					this.materialList = [];
					return;
				}
				const list = Array.isArray(res.data) ? res.data : [];
				const sum = this.summaries.find(s => String(s.id) === String(summaryId));
				const av = this.summaryAvatar(sum || {});
				this.materialList = list.map((row, i) => this.normalizeMarketingContentRow(row, av, i));
			} catch (e) {
				console.error(e);
				this.materialList = [];
			}
		},
		shiftWeek(delta) {
			const d = new Date(this.weekStart);
			d.setDate(d.getDate() + delta * 7);
			d.setHours(0, 0, 0, 0);
			this.weekStart = d;
		},
		applyQuickAction(label) {
			if (!this.selectedSummary) return;
			this.submitMaterial({ quickTemplate: label });
		},
		formatMaterialTime(input) {
			if (input == null || input === '') {
				return this.formatMaterialTime(new Date());
			}
			if (typeof input === 'string') {
				const ts = Date.parse(input);
				if (!Number.isNaN(ts)) {
					return this.formatMaterialTime(new Date(ts));
				}
				return input;
			}
			const d = input instanceof Date ? input : new Date(input);
			if (Number.isNaN(d.getTime())) return String(input);
			const y = d.getFullYear();
			const mo = d.getMonth() + 1;
			const day = d.getDate();
			const h = String(d.getHours()).padStart(2, '0');
			const mi = String(d.getMinutes()).padStart(2, '0');
			return `${y}-${mo}-${day} ${h}:${mi}`;
		},
		normalizeMarketingContentRow(row, listAvatar, idx = 0) {
			const ok = row.status !== 'failed';
			const body = ok
				? (row.content || '')
				: `[生成失败] ${row.error_message || '未知错误'}`;
			return {
				id: row.id != null ? String(row.id) : `c-${Date.now()}-${idx}`,
				avatar: listAvatar || this.defaultTopicAvatar,
				createdAt: this.formatMaterialTime(row.created_at),
				content: body,
				rawStatus: row.status,
				userPrompt: row.user_prompt
			};
		},
		async submitMaterial(options = {}) {
			if (!this.selectedSummary) {
				this.$message.warning('请先选择左侧内容');
				return;
			}
			const quick = (options.quickTemplate || '').trim();
			const hint = (this.userHint || '').trim();
			let user_prompt = [quick, hint].filter(Boolean).join('\n');
			if (!user_prompt) {
				this.$message.warning('请输入生成要求或选择快捷指令');
				return;
			}
			if (user_prompt.length > MAX_USER_PROMPT) {
				this.$message.warning(`生成指令最多 ${MAX_USER_PROMPT} 字`);
				return;
			}
			const summarySnap = this.selectedSummary;
			const summaryId = summarySnap.id;
			this.materialSending = true;
			try {
				const res = await this.$post(
					`/marketing/summary/${summaryId}/content`,
					{
						bot_number: this.botNumber,
						user_prompt
					}
				);
				if (res.code !== 200) {
					this.$message.warning(this.apiMsg(res) || '生成失败');
					return;
				}
				const d = res.data;
				if (!d) {
					this.$message.warning('未返回数据');
					return;
				}
				const av = this.summaryAvatar(summarySnap);
				const row = this.normalizeMarketingContentRow(d, av, 0);
				this.materialList = [row, ...this.materialList];
				if (d.status === 'failed') {
					this.$message.warning(d.error_message || '生成失败');
				} else {
					this.$message.success('生成成功');
				}
			} catch (e) {
				console.error(e);
				const msg =
					(e.response && e.response.data && (e.response.data.message || e.response.data.msg)) ||
					e.message ||
					'请求失败';
				this.$message.error(msg);
			} finally {
				this.materialSending = false;
			}
		},
		copyMaterial(item) {
			if (!item || !item.content) return;
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(item.content).then(() => {
					this.$message.success('已复制');
				}).catch(() => {
					this.$message.warning('复制失败');
				});
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@use '@/styles/markdown.scss' as *;

.marketing-materials-tab {
	flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.mm-topics {
	width: 340px;
    height: calc(100vh - 120px);
    padding: 10px;
    border-radius: 10px;
    box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.4);
    background: #fff;
    display: flex;
    flex-direction: column;
	position: fixed;
    left: 30px;
    top: 70px;
    z-index: 9;
}

.mm-topics__controls {
	padding: 8px 10px;
	flex-shrink: 0;
}

.mm-enable-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}

.mm-enable-row__hint {
	color: #888;
	font-size: 12px;
}

.mm-switch {
	position: relative;
	display: inline-block;
	width: 30px;
	height: 16px;
	flex-shrink: 0;

	input {
		opacity: 0;
		width: 0;
		height: 0;
	}

	input:checked + .mm-switch__slider {
		background: #fdc408;
	}

	input:checked + .mm-switch__slider::before {
		transform: translateX(12px);
	}
}

.mm-switch__slider {
	position: absolute;
	cursor: pointer;
	inset: 0;
	background: #ccc;
	border-radius: 22px;
	transition: 0.2s;

	&::before {
		content: '';
		position: absolute;
		height: 14px;
		width: 14px;
		left: 2px;
		top: 1px;
		background: #fff;
		border-radius: 50%;
		transition: transform 0.2s ease;
	}
}

.mm-week-nav {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 4px;
	margin-top: 12px;
	padding: 10px 12px;
	background: #fdc408;
	border-radius: 999px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.mm-week-nav__edge {
	flex-shrink: 0;
	padding: 0;
	margin: 0;
	border: none;
	background: transparent;
	font-size: 12px;
	color: #1a1a1a;
	cursor: pointer;
	white-space: nowrap;
	line-height: 1.2;

	&:hover {
		opacity: 0.82;
	}

	&:active {
		opacity: 0.68;
	}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	&:disabled:hover,
	&:disabled:active {
		opacity: 0.45;
	}
}

.mm-week-nav__range {
	flex: 1;
	min-width: 0;
	text-align: center;
	font-size: 11px;
	font-weight: 600;
	color: #1a1a1a;
	line-height: 1.3;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.mm-topics__list {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 10px;
	display: flex;
	flex-direction: column;
	gap: 10px;

	&::-webkit-scrollbar {
		display: none;
	}

	-ms-overflow-style: none;
	scrollbar-width: none;
}

.mm-topics__hint {
	text-align: center;
	padding: 16px 8px;
	font-size: 12px;
	color: #999;
}

.mm-topic-card {
	text-align: left;
	width: 100%;
	padding: 10px;
	border: 1px solid #e8e8e8;
	border-radius: 10px;
	background: #fff;
	cursor: pointer;
	margin-bottom: 10px;
	transition: border-color 0.2s, box-shadow 0.2s;

	&:focus {
		outline: 2px solid rgba(253, 196, 8, 0.55);
		outline-offset: 2px;
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}

	&:hover {
		border-color: #ddd;
	}

	&--active {
		border-color: #fdc408;
		box-shadow: 0 0 0 2px rgba(253, 196, 8, 0.2);
	}

	&--failed {
		opacity: 0.75;
	}
}

.mm-topic-card__head {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
}

.mm-topic-card__avatar {
	width: 26px;
	height: 26px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
	border: 1px solid #000;
}

.mm-topic-card__title {
	font-size: 14px;
	font-weight: 600;
	color: #222;
}

/* 信息区：稿图浅灰底 #f5f5f5、内边距约 16～18px；选中态浅黄 #fff9e6 */
.mm-topic-card__body {
	padding: 16px 18px;
	border-radius: 8px;
	background: #f5f5f5;
	transition: background 0.2s ease;
	text-align: left;
}

.mm-topic-card--active .mm-topic-card__body {
	background: #fff1be;
}

.mm-topic-card__summary-md {
	font-size: 12px;
	line-height: 1.55;
	color: #333;
	word-wrap: break-word;
}

.mm-topic-card__md-empty {
	margin: 0;
	color: #999;
	font-size: 12px;
}

.mm-workspace-container {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	background: #f5f5f5;
	box-sizing: border-box;
	/* 右侧与 fixed 的 mm-chat（距右 30px、宽 300px）对齐，与品牌洞察 main-content 留白一致 */
	padding: var(--home-layout-greeting-top, 90px) calc(30px + var(--home-layout-right-rail, 300px)) 28px var(--home-layout-sidebar-inset, 420px);
}

.mm-workspace {
	flex: 1;
	min-width: 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.mm-workspace__inner {
	flex: 1;
	min-height: 0;
	overflow: hidden;
	box-sizing: border-box;
	max-width: var(--home-layout-main-max, 900px);
	width: 100%;
	margin: 0 auto;
	padding: 0 var(--home-layout-main-pad-x, 24px) 24px;
	display: flex;
	flex-direction: column;
	align-items: stretch;
}

.mm-workspace__top {
	flex-shrink: 0;
}

.mm-material-list-scroll {
	flex: 1;
	min-height: 0;
	overflow-x: hidden;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	padding-right: 2px; /* 避免滚动条压住圆角内容 */
}

.mm-greeting {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	margin-bottom: 20px;
	align-self: center;
	width: auto;
	max-width: 100%;
}

.mm-greeting__icon {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	object-fit: cover;
}

.mm-greeting__text {
	margin: 0;
	font-size: 21px;
	color: #333;
}

.mm-context {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 10px 14px;
	padding: 10px 14px;
	background: #ffefb6;
	border-radius: 8px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	font-size: 13px;
	margin-bottom: 16px;

	&--empty {
		background: #fff1be;
	}
}

.mm-context__avatar {
	width: 26px;
	height: 26px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
	border: 1px solid #000;
}

.mm-context__topic {
	font-weight: 600;
	color: #222;
}

.mm-context__check {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: #666;
	font-size: 12px;
	cursor: default;

	input {
		accent-color: #fdc408;
	}
}

.mm-actions {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: flex-start;
	gap: 10px;
	margin-bottom: 18px;
}

.mm-actions__btn {
	padding: 8px 14px;
	font-size: 13px;
	text-align: center;
	border-radius: 6px;
	background: #fff;
	color: #000;
	cursor: pointer;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	transition: box-shadow 0.2s ease, border-color 0.2s ease;

	&:hover:not(:disabled) {
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
		border-color: #ddd;
	}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
}

.mm-input-panel {
	position: relative;
	margin-bottom: 20px;
}

.mm-input-panel--disabled {
	opacity: 0.62;
}

.mm-input-panel__textarea {
	width: 100%;
	box-sizing: border-box;
	padding: 10px 45px 10px 10px;
	border: 1px solid #ffd700;
	border-radius: 12px;
	background: #f2f2f2;
	color: #333;
	font-size: 13px;
	line-height: 1.5;
	resize: vertical;
	min-height: 140px;
	outline: none;
	box-shadow: none;
	resize: none;

	&::placeholder {
		color: #a3a3a3;
	}

	&:focus {
		border-color: #f0c419;
		box-shadow: 0 0 0 1px rgba(255, 215, 0, 0.35);
	}

	&:disabled {
		opacity: 0.65;
		cursor: not-allowed;
	}
}

.mm-input-panel__send {
	position: absolute;
	right: 12px;
	bottom: 12px;
	width: 26px;
	height: 26px;
	border: none;
	border-radius: 50%;
	background: #ffd700;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #fff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

	&:hover:not(:disabled) {
		box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
	}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
		box-shadow: none;
	}
}

.mm-material-list {
	margin: 0;
	padding: 0;
	list-style: none;
}

.mm-material-item {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 30px;
}

.mm-material-item__avatar {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
}

.mm-material-item__col {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.mm-material-item__head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}

.mm-material-item__time {
	font-size: 12px;
	color: #888;
	flex-shrink: 0;
}

.mm-material-item__copy {
	padding: 4px 14px;
	font-size: 12px;
	line-height: 1.4;
	color: #fff;
	border: none;
	border-radius: 6px;
	background: #ffd700;
	cursor: pointer;
	flex-shrink: 0;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

	&:hover {
		filter: brightness(0.95);
	}
}

.mm-material-item__body {
	margin: 0;
	font-size: 13px;
	line-height: 1.65;
	color: #333;
	white-space: pre-wrap;
	word-break: break-word;
	font-family: inherit;
}

.mm-material-item--failed .mm-material-item__body {
	color: #c62828;
}

.mm-chat {
	position: fixed;
	right: 20px;
	top: 70px;
	z-index: 9;
	width: var(--home-layout-right-rail, 300px);
	height: calc(100vh - 120px);
	padding: 0;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow: hidden;
}

.mm-chat__title {
	margin: 0;
	padding: 14px 16px;
	font-size: 13px;
	flex-shrink: 0;
	text-align: center;
	font-weight: normal;
}

.mm-chat__thread {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 12px 10px;
	display: flex;
	flex-direction: column;
	gap: 14px;
	background: #f3f3f3;
	border: 1px solid #5f5f5f;
	border-radius: 8px;
}

.mm-chat__empty {
	text-align: center;
	padding: 20px 8px;
	font-size: 12px;
	color: #999;
}

.mm-msg {
	display: flex;
	gap: 8px;
	align-items: flex-start;

	&--user {
		flex-direction: row-reverse;
	}

	&--assistant .mm-msg__col {
		justify-content: flex-start;
	}

	&--user .mm-msg__col {
		justify-content: flex-end;
	}
}

.mm-msg__avatar {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
	border: 1px solid #000;
	box-sizing: border-box;
}

.mm-msg__col {
	flex: 1;
	min-width: 0;
	display: flex;
}

.mm-msg__body {
	display: flex;
	flex-direction: column;
	max-width: 78%;
	gap: 4px;
}

.mm-msg--assistant .mm-msg__body {
	align-items: flex-start;
}

.mm-msg--user .mm-msg__body {
	align-items: flex-end;
}

.mm-msg__time {
	font-size: 10px;
	line-height: 1.2;
	color: #c8c8c8;
	margin: 0;
	padding: 0 2px;
	white-space: nowrap;
}

.mm-msg__bubble {
	position: relative;
	width: fit-content;
	max-width: 100%;
	padding: 6px 10px;
	border-radius: 6px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.mm-msg--assistant .mm-msg__bubble {
	background: #fff;
}

.mm-msg--assistant .mm-msg__bubble::after {
	content: '';
	position: absolute;
	left: -5px;
	top: 10px;
	border: 5px solid transparent;
	border-right-color: #fff;
}

.mm-msg--user .mm-msg__bubble {
	background: #fff2a8;
}

.mm-msg--user .mm-msg__bubble::after {
	content: '';
	position: absolute;
	right: -5px;
	top: 10px;
	border: 5px solid transparent;
	border-left-color: #fff2a8;
}

.mm-msg__text {
	margin: 0;
	font-size: 12px;
	line-height: 1.45;
	color: #333;
	white-space: pre-wrap;
	word-break: break-word;
}
</style>
